import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import ReactMarkdown from "react-markdown";
import logoUrl from "@/assets/logo/m-icon.png";
import { Button } from "@/components/ui/button";

type ChatMessage = { role: "user" | "assistant"; content: string };

const STORAGE_KEY = "madc_chat_messages_v1";
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

const WELCOME: ChatMessage = {
  role: "assistant",
  content:
    "Hi! 👋 I'm the Mutha's Advanced Dental Care Assistant. Ask me about our dental treatments, timings, or how to book an appointment with Dr. Khushboo Mutha.",
};

const AIChatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as ChatMessage[];
        if (Array.isArray(parsed) && parsed.length > 0) setMessages(parsed);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch {
      /* ignore */
    }
  }, [messages]);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  // Focus input on open
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setError(null);
    const next = [...messages, { role: "user" as const, content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);

    // Strip welcome from API payload (it's static)
    const apiMessages = next.filter((m, i) => !(i === 0 && m === WELCOME));

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ messages: apiMessages }),
      });

      if (!res.ok || !res.body) {
        const errTxt = await res.text().catch(() => "");
        throw new Error(errTxt || `Request failed (${res.status})`);
      }

      // Add empty assistant message we'll fill in
      setMessages((m) => [...m, { role: "assistant", content: "" }]);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith("data:")) continue;
          const data = trimmed.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const json = JSON.parse(data);
            const delta = json.choices?.[0]?.delta?.content;
            if (delta) {
              assistantText += delta;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: assistantText };
                return copy;
              });
            }
          } catch {
            /* skip malformed chunk */
          }
        }
      }

      if (!assistantText) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = {
            role: "assistant",
            content: "Sorry, I didn't catch that. Could you rephrase?",
          };
          return copy;
        });
      }
    } catch (e) {
      const msg = (e as Error).message || "Something went wrong";
      setError(msg.includes("429") ? "Too many requests, please wait a moment." : msg);
      setMessages((m) => {
        // remove the empty assistant placeholder if present
        if (m[m.length - 1]?.role === "assistant" && m[m.length - 1].content === "") {
          return m.slice(0, -1);
        }
        return m;
      });
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetChat = () => {
    setMessages([WELCOME]);
    setError(null);
  };

  return (
    <>
      {/* Floating launcher button */}
      <Button
        size="icon"
        type="button"
        aria-label={open ? "Close chat" : "Open chat"}
        onClick={() => setOpen((o) => !o)}
        className="fixed right-16 top-2 md:top-auto md:right-auto md:left-4 md:bottom-6 z-50 h-11 w-11 md:h-14 md:w-14 p-0 rounded-full bg-primary shadow-soft ring-1 ring-primary/20 transition-transform overflow-hidden"
      >
        {open ? (
          <X className="h-6 w-6 text-primary-foreground" />
        ) : (
          <img src={logoUrl} alt="Mutha's Advanced Dental Care chat" className="h-full w-full object-cover" />
        )}
      </Button>

      {/* Chat panel */}
      {open && (
        <div
          className="fixed left-4 top-20 md:top-auto md:bottom-24 z-50 w-[calc(100vw-2rem)] sm:w-[380px] h-[min(70dvh,560px)] max-h-[calc(100dvh-10rem)] bg-background border border-border rounded-lg shadow-soft-lg flex flex-col overflow-hidden"
          role="dialog"
          aria-label="Mutha's Advanced Dental Care AI Assistant"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-primary text-primary-foreground">
            <img src={logoUrl} alt="" className="h-9 w-9 rounded-full object-cover ring-2 ring-primary-foreground/30" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm leading-tight">Mutha's Advanced Dental Care Assistant</div>
              <div className="text-[11px] opacity-80">Mutha's Advanced Dental Care</div>
            </div>
            <button
              type="button"
              onClick={resetChat}
              className="text-[11px] underline opacity-90 hover:opacity-100"
              aria-label="Reset conversation"
            >
              Reset
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-muted/30">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={
                    m.role === "user"
                      ? "max-w-[85%] rounded-2xl rounded-br-sm bg-primary text-primary-foreground px-3 py-2 text-sm"
                      : "max-w-[90%] text-sm text-foreground"
                  }
                >
                  {m.role === "assistant" ? (
                    <div className="prose prose-sm max-w-none prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5">
                      <ReactMarkdown>{m.content || "…"}</ReactMarkdown>
                    </div>
                  ) : (
                    <span className="whitespace-pre-wrap">{m.content}</span>
                  )}
                </div>
              </div>
            ))}
            {loading && messages[messages.length - 1]?.role !== "assistant" && (
              <div className="flex justify-start">
                <div className="text-xs text-muted-foreground italic">Thinking…</div>
              </div>
            )}
            {error && (
              <div className="text-xs text-destructive bg-destructive/10 rounded-md px-2 py-1.5">
                {error}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-border p-2 bg-background">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Ask about treatments, timings, booking…"
                rows={1}
                className="flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 max-h-32"
                disabled={loading}
              />
              <button
                type="button"
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="h-9 w-9 shrink-0 rounded-lg bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatbot;